import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

/**
 * LanguageContext.js – Lazy-Loading-Architektur
 *
 * Jede Sprache liegt als eigenständige Datei unter src/lang/{code}.js.
 * Webpack/Vite erzeugen daraus separate Chunks (~28 KB pro Sprache).
 *
 * Ladestrategie beim App-Start:
 *   1. Deutsch (DE) wird immer vorgeladen — HejVi-Standardsprache.
 *   2. Gespeicherte Nutzersprache wird parallel geladen.
 *   3. Falls Nutzersprache = DE: Englisch (EN) wird als zweite Sprache geladen
 *      (sichert den Fallback-Mechanismus in t() ab).
 *
 * Sprachwechsel:
 *   - Zielsprache wird geladen, dann erst umgeschaltet → kein Text-Flash.
 *   - loadCounter-Ref verhindert Race-Conditions bei schnellem Wechsel.
 *
 * Cache:
 *   translationCache ist modulscoped → überlebt Re-Renders und StrictMode-Doppelaufrufe.
 *
 * API (identisch zu vorher — keine Änderungen in anderen Komponenten nötig):
 *   const { t, currentLanguage, changeLanguage, isLoadingLang } = useLanguage();
 *   t('adm_save')   →  'Speichern'
 *   t('count_hint', { count: 5 })  →  'Dokument hat 5 Seiten'
 *
 * WICHTIG: isLoadingLang (nicht isLoading!) — um Konflikte mit anderen
 * Loading-States (Video, Uploads etc.) in der App zu vermeiden.
 * isLoading bleibt als Alias für Rückwärtskompatibilität während der Migration.
 */

// ── Modul-Level-Cache: überlebt Re-Renders ───────────────────────────────────
const translationCache = {};

/**
 * Lädt eine Sprachdatei und legt sie in den Cache.
 * Wiederholte Aufrufe geben den gecachten Wert sofort zurück (kein Netzwerk-Request).
 */
const loadLang = async (code) => {
  if (translationCache[code]) {
    return translationCache[code];
  }
  try {
    const mod = await import(`../lang/${code}.js`);
    translationCache[code] = mod.default;
    return mod.default;
  } catch (err) {
    console.warn(`[LanguageContext] Sprachdatei nicht gefunden: lang/${code}.js`, err);
    return translationCache['de'] || translationCache['en'] || {};
  }
};

/** Alle gültigen Sprachcodes. */
const VALID_CODES = new Set([
  'de', 'en', 'nl', 'fr', 'ru', 'pl', 'ar', 'es',
  'ro', 'tr', 'hr', 'it', 'sq', 'bg', 'uk', 'cs', 'el',
]);

const apiLanguageMap = {
  1: 'de', 2: 'en', 3: 'ru', 4: 'ar',  5: 'fr',
  6: 'es', 7: 'pl', 8: 'ro', 9: 'tr', 10: 'hr',
  11: 'sq', 12: 'bg', 13: 'it', 14: 'uk', 15: 'cs', 16: 'el',
};

const staticNames = {
  de: 'Deutsch',    en: 'English',    nl: 'Nederlands', fr: 'Français',
  ru: 'Русский',    pl: 'Polski',     ar: 'العربية',    es: 'Español',
  ro: 'Română',     tr: 'Türkçe',     hr: 'Hrvatski',   it: 'Italiano',
  sq: 'Shqip',      bg: 'Български',  uk: 'Українська', cs: 'Čeština',
  el: 'Ελληνικά',
};

const staticFlags = {
  de: '🇩🇪', en: '🇺🇸', nl: '🇳🇱', fr: '🇫🇷', ru: '🇷🇺', pl: '🇵🇱',
  ar: '🇸🇦', es: '🇪🇸', ro: '🇷🇴', tr: '🇹🇷', hr: '🇭🇷', it: '🇮🇹',
  sq: '🇦🇱', bg: '🇧🇬', uk: '🇺🇦', cs: '🇨🇿', el: '🇬🇷',
};

const STATIC_LANGUAGE_LIST = [
  { id: 1,  name: 'Deutsch',     code: 'de', flag: '🇩🇪' },
  { id: 2,  name: 'English',     code: 'en', flag: '🇺🇸' },
  { id: 3,  name: 'Русский',     code: 'ru', flag: '🇷🇺' },
  { id: 4,  name: 'العربية',     code: 'ar', flag: '🇸🇦' },
  { id: 5,  name: 'Français',    code: 'fr', flag: '🇫🇷' },
  { id: 6,  name: 'Español',     code: 'es', flag: '🇪🇸' },
  { id: 7,  name: 'Polski',      code: 'pl', flag: '🇵🇱' },
  { id: 8,  name: 'Română',      code: 'ro', flag: '🇷🇴' },
  { id: 9,  name: 'Türkçe',      code: 'tr', flag: '🇹🇷' },
  { id: 10, name: 'Hrvatski',    code: 'hr', flag: '🇭🇷' },
  { id: 11, name: 'Shqip',       code: 'sq', flag: '🇦🇱' },
  { id: 12, name: 'Български',   code: 'bg', flag: '🇧🇬' },
  { id: 13, name: 'Italiano',    code: 'it', flag: '🇮🇹' },
  { id: 14, name: 'Українська',  code: 'uk', flag: '🇺🇦' },
  { id: 15, name: 'Čeština',     code: 'cs', flag: '🇨🇿' },
  { id: 16, name: 'Ελληνικά',   code: 'el', flag: '🇬🇷' },
];

// ════════════════════════════════════════════════════════════════════════════════

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('de');
  const [languageName, setLanguageName]       = useState('Deutsch');
  const [availableLanguages, setAvailableLanguages] = useState([]);

  /**
   * isLoadingLang — explizit von "isLoading" umbenannt, um Konflikte mit anderen
   * Loading-States (Video-Rendering, Upload-Progress, API-Calls etc.) zu vermeiden.
   */
  const [isLoadingLang, setIsLoadingLang] = useState(true);

  /**
   * loadCounter verhindert Race-Conditions bei schnellem Sprachwechsel:
   * Nur der zuletzt gestartete Ladevorgang darf setCurrentLanguage aufrufen.
   */
  const loadCounter = useRef(0);

  // ── Initialladung ────────────────────────────────────────────────────────────
  useEffect(() => {
    const savedCode = localStorage.getItem('hejvi_language') || 'de';
    const savedName = localStorage.getItem('hejvi_language_name') || 'Deutsch';
    const validCode = VALID_CODES.has(savedCode) ? savedCode : 'de';

    /**
     * Ladestrategie:
     *   - DE immer laden (HejVi-Standard, primärer Fallback in t()).
     *   - Nutzersprache parallel laden, falls ≠ DE.
     *   - Nutzersprache = DE → EN als zweite Sprache laden (t()-Fallback-Kette).
     */
    const toPreload = validCode === 'de'
      ? ['de', 'en']        // DE-Nutzer: DE + EN für vollständige Fallback-Kette
      : ['de', validCode];  // Anderer Nutzer: DE + Nutzersprache

    setIsLoadingLang(true);

    Promise.all(toPreload.map(loadLang))
      .then(() => {
        setCurrentLanguage(validCode);
        setLanguageName(savedName);
        document.documentElement.lang = validCode;
        document.documentElement.dir  = validCode === 'ar' ? 'rtl' : 'ltr';
        setIsLoadingLang(false);
      })
      .catch((err) => {
        console.error('[LanguageContext] Fehler bei Initialladung:', err);
        setIsLoadingLang(false); // Nie im isLoadingLang:true stecken bleiben
      });

    // Cache-Invalidierung: wenn die gespeicherte Version < 16 ist,
    // alten Cache verwerfen und auf frische API-Daten warten.
    const cacheVersion = localStorage.getItem('hejvi_available_languages_v');
    const cached = localStorage.getItem('hejvi_available_languages');
    if (cached && cacheVersion === '16') {
      try { setAvailableLanguages(JSON.parse(cached)); } catch (_) {}
    } else {
      // Veralteten Cache löschen — wird durch nächsten API-Call neu befüllt
      localStorage.removeItem('hejvi_available_languages');
      localStorage.removeItem('hejvi_available_languages_v');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── available_languages persistieren ────────────────────────────────────────
  /**
   * Aktualisiert die verfügbaren Sprachen aus der API.
   * Ergänzt fehlende Sprachen aus STATIC_LANGUAGE_LIST, damit die Auswahl
   * immer vollständig ist — auch wenn der DB-Patch noch nicht alle Spalten
   * befüllt hat oder der localStorage-Cache veraltet ist.
   */
  const setContextLanguages = (langs) => {
    // Bekannte codes aus der API-Antwort
    const apiCodes = new Set(
      langs.map(l => l.language_code).filter(Boolean)
    );
    // Fehlende Sprachen aus der statischen Liste ergänzen
    const supplemented = [...langs];
    for (const s of STATIC_LANGUAGE_LIST) {
      if (!apiCodes.has(s.code)) {
        supplemented.push({
          id:            s.id,
          language_code: s.code,
          original_name: s.name,
          english_name:  s.name,
          german_name:   s.name,
          flag_url:      s.flag,    // Emoji als Fallback
          status:        1,
          writing_direction: 0,
        });
      }
    }
    // Nach id sortieren
    supplemented.sort((a, b) => (a.id || 99) - (b.id || 99));
    setAvailableLanguages(supplemented);
    localStorage.setItem('hejvi_available_languages', JSON.stringify(supplemented));
    // Versions-Marker: zwingt alten Cache zur Invalidierung
    localStorage.setItem('hejvi_available_languages_v', '16');
  };

  // ── Sprachwechsel ─────────────────────────────────────────────────────────────
  const changeLanguage = async (input) => {
    if (!input) return;

    let targetCode = null;
    let targetName = input;

    if (VALID_CODES.has(input)) {
      targetCode = input;
      const foundLang = availableLanguages.find(l => l.language_code === input);
      targetName = foundLang
        ? (foundLang.original_name || foundLang.english_name)
        : (staticNames[input] || input);
    } else {
      const langObj = availableLanguages.find(l =>
        l.english_name === input || l.original_name === input ||
        l.german_name === input  || l.name === input
      );
      if (langObj) {
        targetCode = langObj.language_code;
        targetName = langObj.original_name || langObj.english_name;
      } else {
        const codeByName = Object.entries(staticNames).find(([, n]) => n === input)?.[0];
        if (codeByName) { targetCode = codeByName; targetName = input; }
      }
    }

    if (!targetCode || !VALID_CODES.has(targetCode)) {
      console.warn(`[LanguageContext] Unbekannte Sprache: "${input}"`);
      return;
    }

    if (targetCode === currentLanguage) return; // bereits aktiv → kein Re-Load

    const myLoad = ++loadCounter.current;
    setIsLoadingLang(true);

    try {
      await loadLang(targetCode);
      if (myLoad !== loadCounter.current) return; // neuerer Aufruf hat überholt

      setCurrentLanguage(targetCode);
      setLanguageName(targetName);
      localStorage.setItem('hejvi_language', targetCode);
      localStorage.setItem('hejvi_language_name', targetName);
      document.documentElement.lang = targetCode;
      document.documentElement.dir  = targetCode === 'ar' ? 'rtl' : 'ltr';
    } catch (err) {
      console.error('[LanguageContext] Fehler beim Laden der Sprache:', err);
    } finally {
      if (myLoad === loadCounter.current) setIsLoadingLang(false);
    }
  };

  // ── Übersetzungsfunktion (synchron, API-kompatibel) ──────────────────────────
  /**
   * Fallback-Kette: Nutzersprache → DE (immer gecacht) → EN (bei DE-Nutzern gecacht) → Key
   */
  const t = (key, params = {}) => {
    const userDict = translationCache[currentLanguage] || {};
    const deDict   = translationCache['de']             || {};
    const enDict   = translationCache['en']             || {};
    const raw = userDict[key] ?? deDict[key] ?? enDict[key] ?? key;

    if (!params || Object.keys(params).length === 0) return raw;
    return raw.replace(/\{(\w+)\}/g, (match, param) => params[param] ?? match);
  };

  // ── Verfügbare Sprachen ───────────────────────────────────────────────────────
  const getAvailableLanguages = () => {
    if (availableLanguages.length > 0) {
      return availableLanguages.map(lang => ({
        id:               lang.id,
        code:             lang.language_code || 'en',
        name:             lang.original_name || lang.english_name || lang.language_code || 'en',
        flag:             lang.flag_url && !lang.flag_url.startsWith('http')
                            ? lang.flag_url                            // bereits Emoji
                            : (lang.flag_url || staticFlags[lang.language_code] || '🌐'),
        originalName:     lang.original_name,
        germanName:       lang.german_name,
        englishName:      lang.english_name,
        writingDirection: lang.writing_direction,
      }));
    }
    return STATIC_LANGUAGE_LIST;
  };

  const getAvailableLanguagesFromLocalStorage = () => getAvailableLanguages();

  const setLanguageFromId = (id) => {
    if (!id) return;
    const code = apiLanguageMap[id];
    if (code) changeLanguage(code);
  };

  const getLanguageFlag = (code) => staticFlags[code] || '🌐';

  // ── Context-Value ─────────────────────────────────────────────────────────────
  const value = {
    currentLanguage,
    languageName,
    t,
    getAvailableLanguages,
    getAvailableLanguagesFromLocalStorage,
    getLanguageFlag,
    setContextLanguages,
    setLanguageFromId,
    changeLanguage,
    isLoadingLang,              // ← Neuer, präziser Name
    isLoading: isLoadingLang,   // ← Alias für Rückwärtskompatibilität
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
